import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { interval, Subscription } from 'rxjs';
import { switchMap, timeout } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';

@Component({
  selector: 'app-copy-store-dialog',
  templateUrl: './copy-store-dialog.component.html',
  styleUrls: ['./copy-store-dialog.component.scss'],
})
export class CopyStoreDialogComponent implements OnInit, OnDestroy {
  @Input() storeId: any;
  @Input() storeName: any;

  newStoreName = new FormControl('', [Validators.required]);
  loading = false;
  polling = false;
  progress: any = null;
  private pollingSubscription: Subscription | null = null;

  constructor(
    private dialogRef: NbDialogRef<CopyStoreDialogComponent>,
    private api: ApiService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.newStoreName.setValue('Copy of ' + this.storeName);
  }

  dismiss(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (this.newStoreName.invalid) {
      this.newStoreName.markAsTouched();
      return;
    }

    this.loading = true;
    this.api
      .postRequest(`${ApiUrl.copyStore}${this.storeId}/copy`, {
        newStoreName: this.newStoreName.value,
      })
      .pipe(timeout(15000))
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          const jobId = res?.response?.data?.jobId ?? res?.data?.jobId;
          this.polling = true;
          this.startPolling(jobId);
        },
        error: (err: any) => {
          this.loading = false;
          let msg = 'Failed to start copy job. Please try again.';
          if (err?.name === 'TimeoutError') {
            msg = 'Request timed out. Please try again.';
          } else {
            msg = err?.error?.response?.message ?? err?.error?.message ?? msg;
          }
          this.toastr.error(msg);
        },
      });
  }

  private startPolling(jobId: string): void {
    const pollUrl = `${ApiUrl.copyJobStatus}${jobId}`;
    this.pollingSubscription = interval(2000)
      .pipe(switchMap(() => this.api.getRequest(pollUrl)))
      .subscribe({
        next: (res: any) => {
          const data = res?.response?.data ?? res?.data ?? res;
          this.progress = data.progress ?? null;

          if (data.status === 'completed') {
            this.stopPolling();
            const stats = data.stats ?? {};
            this.toastr.success(
              `Store copied! ${stats.categoriesCopied ?? 0} categories, ${stats.productsCopied ?? 0} products.`
            );
            this.dialogRef.close({ refresh: true });
            if (data.newStoreId) {
              this.router.navigate(['/editstore', data.newStoreId]);
            }
          }

          if (data.status === 'failed') {
            this.stopPolling();
            this.toastr.error(data.error ?? 'Copy failed. Please try again.');
          }
        },
        error: () => {
          // transient network error — keep polling
        },
      });
  }

  private stopPolling(): void {
    this.polling = false;
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
